import axios from 'axios';
import jwtDecode from 'jwt-decode';
import FuseUtils from '@fuse/FuseUtils';
import { bootInitialCustomIntercomLauncher, getAPIHost, getExpressAPIHost, getRegion, getId, getUserRegion, resolveId } from '../../../utils';
import Split from '../../split';
import _ from 'lodash';
import { signOut, getCurrentSession } from '@milkmoovement/common_cognito_package';

/**
 * Gets the region to use for API requests.
 * Prioritizes the cached region from sessionStorage (set by fetchRegionFromBackend),
 * falls back to URL-based region detection.
 * @returns {string} The region code
 */
const getRegionForAPI = () => {
    // Check sessionStorage first, then localStorage as a fallback.
    // localStorage persists across new browser tabs/pages (same origin),
    // which is critical for Playwright tests that create new pages via
    // context.newPage() — sessionStorage is NOT inherited by new pages.
    const cachedRegion = sessionStorage.getItem('mm.region') || localStorage.getItem('mm.region');
    if (cachedRegion) {
        return cachedRegion;
    }
    return getUserRegion() || getRegion(window.location.hostname);
};

class JWTService extends FuseUtils.EventEmitter {
    _getUserHost = () => {
        // Re-read region from sessionStorage before evaluating the split.
        // During init(), the region may not be cached yet (empty string),
        // causing the split to evaluate OFF → LoopBack host. After login,
        // cacheAuthenticatedRegion() sets mm.region in sessionStorage but
        // does NOT update the Split attribute. This re-sync ensures the
        // split always reflects the latest cached region.
        //
        // If region is still unknown (e.g. new browser tab with empty
        // sessionStorage, or cold start before region-config fetch
        // completes), default to Express — LoopBack is being deprecated
        // and is turned off in some environments (e.g. CDI dev).
        const region = (getRegionForAPI() || '').trim().toUpperCase();
        // Always update Split attribute to prevent stale state from
        // earlier in the SPA lifecycle (e.g. after logout clears
        // sessionStorage but not Split attributes).
        Split.setAttribute('region', region || 'OTHER');
        if (region && region !== 'OTHER') {
            return Split.getBooleanTreatment('use_express_auth_endpoints') ? getExpressAPIHost() : getAPIHost();
        }
        // Region unknown or 'OTHER' — default to Express to avoid 503 from LoopBack
        return getExpressAPIHost();
    };

    init() {
        // Ensure region is set before any split evaluation (e.g. _getUserHost)
        // to avoid cold-start routing to LoopBack when Express is intended.
        Split.setAttribute('region', getRegionForAPI());
        this.setInterceptors();
        this.handleAuthentication();
    }

    setInterceptors = () => {
        axios.interceptors.request.use(async (config) => {
            try {
                // PFM-2805: Defense-in-depth — block token refresh after 8-hour hard cap.
                // TimeoutDialog is the primary enforcer; this catches edge cases where the
                // dialog is unmounted or the cap is reached between interval checks.
                const sessionStartedAt = Number(localStorage.getItem('mm.sessionStartedAt'));
                if (Number.isFinite(sessionStartedAt) && sessionStartedAt > 0 && Date.now() >= sessionStartedAt + 8 * 60 * 60 * 1000) {
                    this.emit('onAutoLogout', 'Session expired');
                    return {
                        ...config,
                        cancelToken: new axios.CancelToken((cancel) => cancel('Session cap reached')),
                    };
                }
                const session = await getCurrentSession();
                const jwt = session.getIdToken().getJwtToken();
                // eslint-disable-next-line no-param-reassign
                config.headers.Authorization = `Bearer ${jwt}`;
                // eslint-disable-next-line no-param-reassign
                config.headers.customer = getRegionForAPI();

                // PFM-2761: Scope loginas-id to LoopBack/Express API origins only.
                // loginas-id was added in PFM-2336 and set globally, causing CORS failures
                // on non-API services like metabase-signing-service.
                // loginas header stays global (pre-existing, not causing issues).
                // Use URL parsing (not substring match) so relative axios URLs like
                // '/users/me' resolve against the configured baseURL, and hostname
                // equality prevents spoofing via substring overlap.
                const requestUrl = config.url || '';
                const apiHost = getAPIHost();
                const expressHost = getExpressAPIHost();
                let isApiRequest = false;
                try {
                    const resolved = new URL(requestUrl, config.baseURL || apiHost);
                    const apiHostname = new URL(apiHost).hostname;
                    const expressHostname = new URL(expressHost).hostname;
                    isApiRequest = resolved.hostname === apiHostname || resolved.hostname === expressHostname;
                } catch (e) {
                    // If URL parsing fails, fall back to substring match (safe default:
                    // better to attach loginas-id than to silently drop it).
                    isApiRequest = requestUrl.includes(apiHost) || requestUrl.includes(expressHost);
                }
                if (isApiRequest) {
                    const loginasId = sessionStorage.getItem('selected_user');
                    if (loginasId) {
                        // eslint-disable-next-line no-param-reassign
                        config.headers['loginas-id'] = loginasId;
                    }
                }

                return config;
            } catch (e) {
                if (e.message === 'No current user') {
                    // This occurs post manual logout typically and we don't want to display a message
                    this.emit('onAutoLogout', null);
                } else {
                    this.emit('onAutoLogout', 'Token expired');
                }
                return {
                    ...config,
                    cancelToken: new axios.CancelToken((cancel) => cancel('User unauthenticated')),
                };
            }
        });
        axios.interceptors.response.use(
            (response) => response,
            (err) =>
                new Promise((resolve, reject) => {
                    if (err?.toJSON?.().message === 'Network Error' && !navigator.onLine) {
                        this.emit('onAutoLogout', 'Check your internet connection');
                    }
                    /* eslint-disable-next-line */
                    if (err.response && err.response.status === 401 && err.config && !err.config.__isRetryRequest) {
                        // Only auto-logout on 401 from auth-critical endpoints.
                        // Data endpoint 401s (e.g. /users?filter=...) during impersonation
                        // should not kill the entire session.
                        const url = err.config.url || '';
                        const isAuthEndpoint = url.includes('/users/me') || url.includes('/users/lookup') || url.includes('/login');
                        if (isAuthEndpoint && sessionStorage.getItem('areWeLocked') !== 'locked') {
                            this.emit('onAutoLogout', 'User credentials are not correct');
                        }

                        if (isAuthEndpoint) {
                            this.setSession(null);
                        }
                    }
                    throw err;
                })
        );
    };

    handleAuthentication = async () => {
        try {
            const session = await getCurrentSession();
            const accessToken = session.getIdToken().getJwtToken();

            if (!accessToken) {
                return;
            }

            if (this.isAuthTokenValid(accessToken)) {
                const sessionSet = this.setSession(accessToken);
                if (sessionSet) {
                    this.emit('onAutoLogin', true);
                }
            }
            // eslint-disable-next-line no-empty
        } catch {}
    };

    signInWithCognitoSession = async () => {
        const session = await getCurrentSession();
        const sessionSet = this.setSession(session.getIdToken().getJwtToken());
        if (!sessionSet) {
            throw new Error('Session expired');
        }
        return this.fetchUserData();
    };

    setSession = (accessToken) => {
        if (accessToken) {
            // PFM-2805: Session epoch management.
            // Missing/invalid epoch → initialize (first login).
            // Expired epoch → block session (hard cap reached, do NOT reset).
            const existingEpoch = Number(localStorage.getItem('mm.sessionStartedAt'));
            const now = Date.now();
            const isMissingOrInvalid = !Number.isFinite(existingEpoch) || existingEpoch <= 0 || existingEpoch > now + 60000;
            const isExpired = !isMissingOrInvalid && now >= existingEpoch + 8 * 60 * 60 * 1000;
            if (isExpired) {
                this.emit('onAutoLogout', 'Session expired');
                return false;
            }
            if (isMissingOrInvalid) {
                localStorage.setItem('mm.sessionStartedAt', String(Date.now()));
            }
            axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            axios.defaults.headers.customer = getRegionForAPI();
            axios.defaults.crossDomain = true;
            const loginas = sessionStorage.getItem('selected_email');
            if (loginas) {
                axios.defaults.headers.common.loginas = loginas;
            } else {
                delete axios.defaults.headers.common.loginas;
            }
            return true;
        }
        delete axios.defaults.headers.common.Authorization;
        delete axios.defaults.headers.common.loginas;
        return true;
    };

    logout = async () => {
        this.setSession(null);
        const isLocked = sessionStorage.getItem('areWeLocked') === 'locked';
        try {
            if (!isLocked) {
                await signOut();
            }
        } finally {
            sessionStorage.clear();
            localStorage.removeItem('mm.sessionStartedAt');
            window.Intercom('shutdown');
            bootInitialCustomIntercomLauncher();
        }
    };

    lock = async () => {
        const clearUsername = false;
        await signOut(clearUsername);
    };

    isAuthTokenValid = (accessToken) => {
        if (!accessToken) {
            return false;
        }
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;
        return decoded.exp >= currentTime;
    };

    fetchUserData = async () => {
        const response = await axios.get(`${this._getUserHost()}/users/me`);
        const data = resolveId(response.data);
        const { role, subuser } = data;
        if (role === 'admin') {
            sessionStorage.setItem('isAdmin', 'true');
            const impersonationAllowed = data?.permissions?.includes('CanImpersonateUsers');
            sessionStorage.setItem('impersonationAllowed', impersonationAllowed ? 'true' : 'false');
        } else {
            sessionStorage.removeItem('isAdmin');
            sessionStorage.removeItem('impersonationAllowed');
        }
        if (role === 'driver' || role === 'receiver') {
            this.setSession(null);
            const error = new Error(`${_.capitalize(role)}s please login through the driver handheld.`);
            error.name = 'RoleError';
            throw error;
        }

        if (subuser) {
            try {
                const parentName = await this.getParentName(data);
                sessionStorage.setItem('user_parent_name', parentName);
            } catch {
                sessionStorage.removeItem('user_parent_name');
            }
        }

        return {
            role,
            data,
        };
    };

    getParentName = async (userData) => {
        const parentId = getId();
        // PFM-2805: Guard against undefined ID (guest, partial rehydration, missing role mapping)
        if (!parentId) {
            return userData.name;
        }
        // if not a subuser or is a sub admin return their own name
        if (parentId === userData.id || !userData.subuser || userData.role === 'admin') {
            return userData.name;
        }

        try {
            const { data: parentResponse } = await axios.get(`${this._getUserHost()}/users/${parentId}`);
            const parentData = resolveId(parentResponse);
            return parentData.name;
        } catch {
            return userData.name;
        }
    };

    setMainUser = async (mainUser) => {
        sessionStorage.setItem('main_user_id', mainUser.id);
        const parentName = await this.getParentName(mainUser);
        sessionStorage.setItem('main_user_name', parentName);
    };

    signInAsImpersonatedUser = async (id, mainUser = null) => {
        // PFM-2805: Prevent /users/undefined requests
        if (!id) {
            throw new Error('Cannot impersonate without a user ID');
        }
        if (mainUser && !sessionStorage.getItem('main_user_id')) {
            await this.setMainUser(mainUser);
        }
        const response = await axios.get(`${this._getUserHost()}/users/${id}`);
        const data = resolveId(response.data);
        const { email, role } = data;
        axios.defaults.headers.common.loginas = email;
        sessionStorage.setItem('isImpersonating', 'true');
        sessionStorage.setItem('selected_user', data.id);
        sessionStorage.setItem('selected_name', data.name);
        sessionStorage.setItem('selected_email', email);
        return { role, data };
    };

    revertImpersonatedLogin = async () => {
        delete axios.defaults.headers.common.loginas;
        // Clear impersonation state BEFORE the API call so the request
        // interceptor does not attach loginas-id to /users/me.
        // Previously these removeItem calls were after the API call,
        // so the interceptor still found 'selected_user' in sessionStorage
        // and attached loginas-id — causing the backend to return the
        // impersonated user's data instead of the real admin's.
        sessionStorage.removeItem('isImpersonating');
        sessionStorage.removeItem('selected_user');
        sessionStorage.removeItem('selected_name');
        sessionStorage.removeItem('selected_email');
        sessionStorage.removeItem('main_user_id');
        sessionStorage.removeItem('main_user_name');

        const response = await axios.get(`${this._getUserHost()}/users/me`);
        const data = resolveId(response.data);
        const { role } = data;
        if (role === 'admin') {
            sessionStorage.setItem('isAdmin', 'true');
            const impersonationAllowed = data?.permissions?.includes('CanImpersonateUsers');
            sessionStorage.setItem('impersonationAllowed', impersonationAllowed ? 'true' : 'false');
        } else {
            sessionStorage.removeItem('isAdmin');
            sessionStorage.removeItem('impersonationAllowed');
        }
        return { role, data };
    };
}

// eslint-disable-next-line new-cap
const instance = new JWTService();

export default instance;
